import { useState } from 'react';
import { dossierApi } from '../../api/resources';
import { tr } from '../../lib/i18n';
import { estEnregistre } from '../../lib/medias';
import {
  etatDesPieces,
  nouvellePiece,
  pieceDuCote,
  prepareLaPiece,
} from '../../lib/pieces-employe';
import { C } from '../../theme/palette';

const ligne = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  border: '1px solid ' + C.border,
  borderRadius: 10,
  padding: '9px 10px',
};

const bouton = (plein, occupe) => ({
  flexShrink: 0,
  display: 'inline-block',
  border: plein ? 'none' : '1px solid ' + C.border,
  background: plein ? C.navy : C.white,
  color: plein ? '#fff' : C.ink,
  borderRadius: 9,
  padding: '9px 10px',
  fontSize: 10.5,
  fontWeight: 700,
  cursor: occupe ? 'wait' : 'pointer',
  opacity: occupe ? 0.6 : 1,
});

const croix = {
  background: 'none',
  border: 'none',
  color: C.red,
  fontSize: 10,
  fontWeight: 800,
  cursor: 'pointer',
  padding: '0 2px',
};

/**
 * « 📎 Pièces de l'employé » : sa carte d'identité (recto, verso) et sa carte
 * AVS, prises en photo au téléphone ou jointes en PDF, dès la création de la
 * fiche.
 *
 * <p>Rien n'est gardé à part : chaque fichier va sur une pièce du dossier
 * (`documents`), celle de la liste « 📁 Dossier documents ». Sur une fiche
 * enregistrée dont la pièce existe, le fichier part au serveur tout de suite,
 * comme depuis le dossier ; sinon il attend sur la pièce, et part à
 * l'enregistrement de la fiche.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.f fiche en cours de saisie
 * @param {(fn: (p: object) => object) => void} props.setEmpForm mise à jour de la fiche
 * @returns {import('react').ReactElement} le bloc
 */
export function PiecesEmploye({ f, setEmpForm }) {
  const [envoi, setEnvoi] = useState(null);
  const [erreur, setErreur] = useState('');
  // Après « Refaire » sur le recto, le bouton propose le verso.
  const [suite, setSuite] = useState(null);
  const docs = f.documents || [];
  const etat = etatDesPieces(docs);
  const enAttente = docs.some((d) => d?.aJoindre);

  const majDocs = (fn) => setEmpForm((p) => ({ ...p, documents: fn([...(p.documents || [])]) }));

  const choisit = async (cote, evenement) => {
    const fichier = evenement.target.files && evenement.target.files[0];
    evenement.target.value = '';
    if (!fichier) return;
    setErreur('');
    setEnvoi(cote);
    try {
      const pret = await prepareLaPiece(fichier);
      const ix = pieceDuCote(docs, cote);
      const doc = ix >= 0 ? docs[ix] : null;
      if (doc && estEnregistre(f.id) && estEnregistre(doc.id)) {
        // La pièce existe au serveur : le fichier y part tout de suite.
        const dto = await dossierApi.joindre(f.id, doc.id, pret, fichier.name);
        majDocs((l) => {
          const i = l.findIndex((d) => d.id === doc.id);
          if (i >= 0) {
            l[i] = {
              ...l[i],
              fichier: dto?.fichierJoint === true,
              nomFichier: dto?.nomFichier || '',
            };
          }
          return l;
        });
      } else {
        const aJoindre = { fichier: pret, nom: fichier.name };
        majDocs((l) => {
          if (ix >= 0 && l[ix]) l[ix] = { ...l[ix], aJoindre };
          else l.push({ ...nouvellePiece(cote), aJoindre });
          return l;
        });
      }
      setSuite(cote === 'recto' && etat.verso >= 0 ? 'verso' : null);
    } catch (e) {
      setErreur(e?.message || 'L’envoi a échoué.');
    } finally {
      setEnvoi(null);
    }
  };

  const retire = async (ix) => {
    const doc = docs[ix];
    if (!doc) return;
    setErreur('');
    if (doc.aJoindre) {
      majDocs((l) =>
        doc.parLeBloc && !estEnregistre(doc.id)
          ? l.filter((d) => d !== doc)
          : l.map((d) => (d === doc ? { ...d, aJoindre: undefined } : d)),
      );
      return;
    }
    if (!window.confirm('Retirer ce fichier ? Il sera effacé du serveur.')) return;
    try {
      await dossierApi.retire(f.id, doc.id);
      majDocs((l) =>
        l.map((d) => (d.id === doc.id ? { ...d, fichier: false, nomFichier: '' } : d)),
      );
    } catch (e) {
      setErreur(e?.message || 'Le retrait a échoué.');
    }
  };

  const fait = (texte, ix, libelle) => (
    <span style={{ whiteSpace: 'nowrap' }}>
      <span>{texte}</span>{' '}
      <button type="button" aria-label={libelle} onClick={() => retire(ix)} style={croix}>
        ✕
      </button>
    </span>
  );
  const aAjouter = <span style={{ color: C.amber }}>à ajouter</span>;

  const choix = (cote, libelle, plein, aria) => (
    <label style={bouton(plein, envoi != null)}>
      {envoi === cote ? 'Envoi…' : libelle}
      <input
        type="file"
        accept="image/*,application/pdf"
        aria-label={aria}
        disabled={envoi != null}
        style={{ display: 'none' }}
        onChange={(e) => choisit(cote, e)}
      />
    </label>
  );

  // Un seul bouton pour la carte : le recto d'abord, puis le verso ; « Refaire »
  // reprend le recto, puis propose le verso.
  const coteCarte =
    etat.recto < 0 ? 'recto' : etat.verso < 0 || suite === 'verso' ? 'verso' : 'recto';
  const libelleCarte =
    etat.recto < 0 ? '📷 Photo / PDF' : coteCarte === 'verso' ? '📷 Verso' : '📷 Refaire';

  return (
    <div
      data-testid="pieces-employe"
      style={{
        border: '2px solid ' + C.teal,
        borderRadius: 12,
        padding: 11,
        margin: '8px 0 4px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 800, color: C.ink }}>📎 Pièces de l'employé</div>
      <div style={{ fontSize: 9.5, color: C.grey, lineHeight: 1.5, marginTop: -4 }}>
        Une photo au téléphone ou un scan PDF. Elles vont directement dans son dossier.
      </div>
      <div style={ligne}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 800 }}>🪪 Carte d'identité</span>
          <span style={{ fontSize: 9, color: C.muted }}>ou passeport / permis de séjour</span>
          <span
            data-testid="etat-identite"
            style={{ fontSize: 9.5, fontWeight: 700, color: C.green, marginTop: 1 }}
          >
            {etat.recto < 0 && etat.verso < 0 ? (
              aAjouter
            ) : (
              <>
                {etat.recto >= 0 && fait('✓ recto', etat.recto, 'Retirer le recto')}
                {etat.recto >= 0 && etat.verso >= 0 && ' · '}
                {etat.verso >= 0 && fait('✓ verso', etat.verso, 'Retirer le verso')}
              </>
            )}
          </span>
        </div>
        {choix(
          coteCarte,
          libelleCarte,
          etat.recto < 0 || coteCarte === 'verso',
          "🪪 Carte d'identité",
        )}
      </div>
      <div style={ligne}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 800 }}>🟧 Carte AVS</span>
          <span
            data-testid="etat-avs"
            style={{ fontSize: 9.5, fontWeight: 700, color: C.green, marginTop: 1 }}
          >
            {etat.avs < 0 ? aAjouter : fait('✓ reçue', etat.avs, 'Retirer la carte AVS')}
          </span>
        </div>
        {choix('avs', etat.avs < 0 ? '📷 Photo / PDF' : '📷 Refaire', etat.avs < 0, '🟧 Carte AVS')}
      </div>
      {enAttente && (
        <div role="status" style={{ fontSize: 9, color: C.muted, lineHeight: 1.5 }}>
          {tr('⏳ Part dans son dossier à l’enregistrement de la fiche.')}
        </div>
      )}
      {erreur && (
        <div role="alert" style={{ fontSize: 9.5, color: C.red, lineHeight: 1.5 }}>
          {erreur}
        </div>
      )}
    </div>
  );
}
